import { useEffect, useState } from 'react';
import { PurchaseRestrictionModalProps } from './PurchaseRestrictionModalWrapper';
import { Button, Modal, Text } from '@msc-b2c/shared-components';
import styles from './purchaseRestrictionModal.module.scss';
import {
  countDaysUntil,
  getActiveSession,
  RootState,
  setErrorManagerCode,
  toggleDisplayOverlay,
  updateActiveSession,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { isMultiCabins } from '../utils/utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { isXDaysBeforeDeparture } from '../utils/myAreaUtils';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorManagerCodeEnum } from '@msc-b2c/interfaces/enums';

function PurchaseRestrictionModal({
  title,
  text,
  buttonLabel,
  dataComponentName,
}: Readonly<PurchaseRestrictionModalProps>) {
  const dispatch = useDispatch();
  const { bookingNumber = '', cruiseId, multiCabinRestrictionModal } = getActiveSession();
  const { data: bookingData } = useGetBookingQuery(
    bookingNumber !== '' ? bookingNumber : skipToken
  );

  const departureDate = useSelector(
    (state: RootState) => state.myCruise.itinerary?.departureDate ?? ''
  );
  const [isVisible, setVisible] = useState(false);

  const isPrepaidsClosedModal = (departureDate?: string) => {
    if (!departureDate) return false;

    const remainingDays = countDaysUntil(undefined, departureDate);
    return remainingDays > 0 && isXDaysBeforeDeparture(departureDate);
  };

  useEffect(() => {
    if (isPrepaidsClosedModal(departureDate)) {
      dispatch(setErrorManagerCode(ErrorManagerCodeEnum.PREPAID_CLOSED));
      return;
    }
  }, [departureDate]);

  useEffect(() => {
    if (!bookingData) return;

    // add a safegurd condition bookingData.departureDate === departureDate
    // so we show the modal only when the conditions are true
    // this is because sometimes bookingData takes 2 passes of the useeffect to update
    if (
      isPrepaidsClosedModal(bookingData.departureDate) &&
      bookingData.departureDate === departureDate
    ) {
      dispatch(setErrorManagerCode(ErrorManagerCodeEnum.PREPAID_CLOSED));

      return;
    }
    if (multiCabinRestrictionModal) return;

    if (cruiseId && isMultiCabins(bookingData, cruiseId)) {
      setVisible(true);
      updateActiveSession('multiCabinRestrictionModal', true);
    }
  }, [bookingData, cruiseId]);

  useEffect(() => {
    toggleDisplayOverlay(isVisible);
  }, [isVisible]);

  return (
    isVisible && (
      <Modal
        id={dataComponentName}
        styleOption="cmp-modal__container -modal-private-area"
        onClose={() => setVisible(false)}
      >
        <div className={styles['cmp-container__header']}>
          <Text text={title} />
        </div>
        <div className={styles['cmp-container__body']}>
          <Text text={text} />
        </div>
        <Button
          text={buttonLabel}
          accessibilityLabel={buttonLabel}
          styleOption="cmp-container__button"
          onClick={() => setVisible(false)}
        />
      </Modal>
    )
  );
}

export default PurchaseRestrictionModal;
